import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import NextArrow_img from 'assets/DetailImage/NextArrow.png';
import PrevArrow_img from 'assets/DetailImage/PrevArrow.png';
import default_img from 'assets/DetailImage/defaultImg.png';
import Map from 'components/Map/Map';
import liveTalkIcon from 'assets/DetailImage/liveTalk.png';
import defaultProfileImage from 'assets/MemberImage/defaultProfileImage.png';
import { memberService } from 'services/memberService';
import { facilityService, BuildingFacility, LiveTalk } from 'services/facilityService';
import { Building } from 'types';
import {
  Body,
  BackButton,
  ImageSlider,
  All,
  TextContainer,
  TitleText,
  Time,
  MapInfo,
  DetailTitle,
  DetailContent,
  Map as MapContainer,
  LocationData,
  DataContainer,
  ArrowContainer,
  FacilitiesSection,
  FacilitiesTitle,
  FacilitiesGrid,
  FacilityCard,
  FacilityImageWrapper,
  FacilityImage,
  FacilityInfo,
  FacilityName,
  FacilityLocation,
  FacilityPlaceholder,
  EmptyFacilities,
  LiveTalkSection,
  LiveTalkHeader,
  LiveTalkTitle,
  ViewAllButton,
  LiveTalkList,
  LiveTalkItem,
  LiveTalkItemHeader,
  LiveTalkProfileImage,
  LiveTalkUserInfo,
  LiveTalkUsername,
  LiveTalkTime,
  LiveTalkContent,
  LiveTalkEmpty,
} from './BuildingDetail.styles';

interface ArrowProps {
  onClick?: () => void;
}

function BuildingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [buildingData, setBuildingData] = useState<Building | null>(null);
  const [facilities, setFacilities] = useState<BuildingFacility[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [facilitiesLoading, setFacilitiesLoading] = useState<boolean>(false);
  const [liveTalks, setLiveTalks] = useState<LiveTalk[]>([]);
  const [liveTalksLoading, setLiveTalksLoading] = useState<boolean>(true);

  // Fetch building data
  useEffect(() => {
    const fetchBuildingData = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        const building = await memberService.getBuilding(Number(id));
        setBuildingData(building);
        console.log('Building data:', building);
      } catch (error) {
        console.error('Error fetching building data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBuildingData();
  }, [id]);

  // Fetch facilities
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!id) return;

      try {
        setFacilitiesLoading(true);
        const facilitiesData = await facilityService.getBuildingFacilities(Number(id));
        setFacilities(facilitiesData);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      } finally {
        setFacilitiesLoading(false);
      }
    };

    fetchFacilities();
  }, [id]);

  // Fetch live talks
  useEffect(() => {
    const fetchLiveTalks = async () => {
      if (!id) {
        setLiveTalksLoading(false);
        return;
      }
      try {
        setLiveTalksLoading(true);
        const response = await facilityService.getLiveTalk(Number(id), 0, 3);
        setLiveTalks(response.content);
      } catch (error) {
        console.error('Error fetching live talks:', error);
      } finally {
        setLiveTalksLoading(false);
      }
    };
    fetchLiveTalks();
  }, [id]);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMinutes < 1) return '방금 전';
    if (diffMinutes < 60) return `${diffMinutes}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  const handleFacilityClick = (facilityId: number) => {
    navigate(`/facilities/${facilityId}`);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const settings1 = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  return (
    <Body>
      <BackButton onClick={handleGoBack}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </BackButton>
      <ImageSlider>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <Slider {...settings1}>
            {buildingData?.images && buildingData.images.length > 0 ? (
              buildingData.images.map((image, index) => (
                <div key={index} className="slide-container">
                  <img className="img" src={image} alt={`building${index + 1}`} />
                </div>
              ))
            ) : (
              <div className="slide-container">
                <img className="img" src={default_img} alt="이미지가 없습니다" />
              </div>
            )}
          </Slider>
        )}
      </ImageSlider>

      <All>
        <TextContainer>
          <TitleText>{buildingData?.name}</TitleText>
          <Time>
            {buildingData?.operationTimes && buildingData.operationTimes.length > 0 && (
              <>
                이용가능시간 {buildingData.operationTimes.map((time, index) => (
                  <span key={index}>
                    {index > 0 && ', '}
                    {time.startTime.substring(0, 5)}~{time.endTime.substring(0, 5)}
                  </span>
                ))}
              </>
            )}
          </Time>
        </TextContainer>
      </All>

      <MapInfo>
        <DetailTitle>세부 위치</DetailTitle>
        <DetailContent>{buildingData?.location}</DetailContent>
        <MapContainer>
          {buildingData && (
            <Map
              latitude={buildingData.latitude}
              longitude={buildingData.longitude}
            />
          )}
        </MapContainer>
      </MapInfo>

      {buildingData?.description && (
        <LocationData>
          <DataContainer>
            <DetailTitle>건물 설명</DetailTitle>
            <DetailContent>{buildingData.description}</DetailContent>
          </DataContainer>
        </LocationData>
      )}

      <LiveTalkSection>
        <LiveTalkHeader>
          <LiveTalkTitle>
            실시간 톡 <img src={liveTalkIcon} alt="live talk" style={{ width: '20px', height: '20px', verticalAlign: 'middle' }} />
          </LiveTalkTitle>
          <ViewAllButton onClick={() => navigate(`/buildings/${id}/live-talk`)}>전체보기 &gt;</ViewAllButton>
        </LiveTalkHeader>
        {liveTalksLoading ? (
          <LiveTalkEmpty>로딩 중...</LiveTalkEmpty>
        ) : liveTalks.length === 0 ? (
          <LiveTalkList>
            <LiveTalkItem style={{ minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LiveTalkContent style={{ textAlign: 'center' }}>건의 사항이나 현재 상황, 건물 이용 팁을 공유해주세요!</LiveTalkContent>
            </LiveTalkItem>
          </LiveTalkList>
        ) : (
          <LiveTalkList>
            {liveTalks.map((talk) => (
              <LiveTalkItem key={talk.id}>
                <LiveTalkItemHeader>
                  <LiveTalkProfileImage
                    src={talk.member.profileImage || defaultProfileImage}
                    alt={talk.member.name}
                    referrerPolicy="no-referrer"
                  />
                  <LiveTalkUserInfo>
                    <LiveTalkUsername>{talk.member.name}</LiveTalkUsername>
                    <LiveTalkTime>{formatTimeAgo(talk.createdAt)}</LiveTalkTime>
                  </LiveTalkUserInfo>
                </LiveTalkItemHeader>
                <LiveTalkContent>{talk.message}</LiveTalkContent>
              </LiveTalkItem>
            ))}
          </LiveTalkList>
        )}
      </LiveTalkSection>

      <FacilitiesSection>
        <FacilitiesTitle>이 건물의 시설 🏢</FacilitiesTitle>
        {facilitiesLoading ? (
          <EmptyFacilities>시설 정보 로딩 중...</EmptyFacilities>
        ) : facilities.length === 0 ? (
          <EmptyFacilities>등록된 시설이 없습니다.</EmptyFacilities>
        ) : (
          <FacilitiesGrid>
            {facilities.map((facility) => (
              <FacilityCard key={facility.id} onClick={() => handleFacilityClick(facility.id)}>
                <FacilityImageWrapper>
                  {facility.thumbnailImage ? (
                    <FacilityImage src={facility.thumbnailImage} alt={facility.name} />
                  ) : (
                    <FacilityPlaceholder>🏛️</FacilityPlaceholder>
                  )}
                </FacilityImageWrapper>
                <FacilityInfo>
                  <FacilityName>{facility.name}</FacilityName>
                  <FacilityLocation>{facility.location || '위치 정보 없음'}</FacilityLocation>
                </FacilityInfo>
              </FacilityCard>
            ))}
          </FacilitiesGrid>
        )}
      </FacilitiesSection>
    </Body>
  );
}

const NextArrow: React.FC<ArrowProps> = ({ onClick }) => {
  return (
    <ArrowContainer right="true" onClick={onClick}>
      <img src={NextArrow_img} alt="Next" />
    </ArrowContainer>
  );
};

const PrevArrow: React.FC<ArrowProps> = ({ onClick }) => {
  return (
    <ArrowContainer left="true" onClick={onClick}>
      <img src={PrevArrow_img} alt="Prev" />
    </ArrowContainer>
  );
};

export default BuildingDetail;
